import { COMFORT, RECOMMENDATION } from '@/config/training';
import type { ChordStats, SessionSummary, TransitionStats, TransitionSample } from '@/types/training';

/** BPM values are bucketed before aggregation so per-tempo stats mean something. */
export function bpmBucket(bpm: number): number {
  return Math.round(bpm / COMFORT.bucketSize) * COMFORT.bucketSize;
}

/**
 * Fold new samples into an existing aggregate.
 *
 * Ordered: `C>G` and `G>C` are different skills and are never merged. Anyone who
 * has fought the G chord knows they are not the same difficulty.
 */
export function foldTransition(
  existing: TransitionStats | undefined,
  samples: TransitionSample[],
): TransitionStats | null {
  if (samples.length === 0) return existing ?? null;
  const first = samples[0];

  const base: TransitionStats = existing ?? {
    key: first.key,
    from: first.from,
    to: first.to,
    attempts: 0,
    successes: 0,
    successRate: 0,
    avgSec: null,
    bestSec: null,
    timedCount: 0,
    comfortableBpm: null,
    trend: 0,
    lastPracticedAt: 0,
    byBpm: {},
  };

  const byBpm = { ...base.byBpm };
  let attempts = base.attempts;
  let successes = base.successes;
  let timeCount = countTimed(base);
  let timeSum = (base.avgSec ?? 0) * timeCount;
  let best = base.bestSec;
  let lastAt = base.lastPracticedAt;
  const recent = [...(base.recentOutcomes ?? [])];

  for (const sample of samples) {
    attempts++;
    if (sample.success) successes++;
    recent.push(sample.success ? 1 : 0);
    if (recent.length > RECOMMENDATION.recentWindow) recent.shift();
    if (sample.seconds !== null) {
      timeSum += sample.seconds;
      timeCount++;
      if (best === null || sample.seconds < best) best = sample.seconds;
    }
    if (sample.at > lastAt) lastAt = sample.at;

    const bucket = bpmBucket(sample.bpm);
    const entry = byBpm[bucket] ?? { attempts: 0, successes: 0 };
    byBpm[bucket] = {
      attempts: entry.attempts + 1,
      successes: entry.successes + (sample.success ? 1 : 0),
    };
  }

  const successRate = attempts > 0 ? successes / attempts : 0;

  return {
    ...base,
    attempts,
    successes,
    successRate,
    avgSec: timeCount > 0 ? timeSum / timeCount : null,
    bestSec: best,
    timedCount: timeCount,
    recentOutcomes: recent,
    comfortableBpm: estimateComfortableBpm(byBpm),
    trend: computeTrend(base.successRate, successRate, base.attempts),
    lastPracticedAt: lastAt,
    byBpm,
  };
}

/**
 * How many samples behind `avgSec` carried a measured time.
 *
 * Records written before `timedCount` existed fall back to the old guess, which
 * is wrong but bounded, and self-corrects as soon as the transition is practised
 * again — the alternative, discarding the history, is worse.
 */
function countTimed(stats: TransitionStats): number {
  if (stats.timedCount !== undefined) return stats.timedCount;
  return stats.avgSec === null ? 0 : Math.max(1, stats.successes);
}

/**
 * Comfortable tempo: the fastest bucket where the player still succeeds
 * reliably, requiring the buckets below it to hold up too.
 *
 * Walking upward and stopping at the first failure — rather than taking the
 * best bucket anywhere — prevents one lucky fast session from declaring a tempo
 * the player cannot actually hold.
 */
export function estimateComfortableBpm(
  byBpm: Record<number, { attempts: number; successes: number }>,
): number | null {
  const buckets = Object.keys(byBpm)
    .map(Number)
    .filter((bpm) => byBpm[bpm].attempts >= COMFORT.minAttemptsPerBucket)
    .sort((a, b) => a - b);

  let comfortable: number | null = null;
  for (const bpm of buckets) {
    const entry = byBpm[bpm];
    const rate = entry.successes / entry.attempts;
    if (rate >= COMFORT.successRate) comfortable = bpm;
    else break;
  }
  return comfortable;
}

/** Positive when the recent success rate is above the historical one. */
function computeTrend(previousRate: number, newRate: number, previousAttempts: number): number {
  if (previousAttempts === 0) return 0;
  return Number((newRate - previousRate).toFixed(4));
}

export function foldChordStats(
  existing: ChordStats | undefined,
  chordId: string,
  summary: SessionSummary,
): ChordStats {
  const base: ChordStats = existing ?? {
    chordId,
    attempts: 0,
    correct: 0,
    avgClarity: null,
    lastPracticedAt: 0,
  };

  const chordAttempts = summary.attempts > 0 ? Math.round(summary.attempts / Math.max(1, summary.chordIds.length)) : 0;
  const chordCorrect = summary.correct > 0 ? Math.round(summary.correct / Math.max(1, summary.chordIds.length)) : 0;

  let avgClarity = base.avgClarity;
  if (summary.clarityScore !== null) {
    avgClarity = avgClarity === null ? summary.clarityScore : (avgClarity + summary.clarityScore) / 2;
  }

  return {
    chordId,
    attempts: base.attempts + chordAttempts,
    correct: base.correct + chordCorrect,
    avgClarity,
    lastPracticedAt: Math.max(base.lastPracticedAt, summary.startedAt),
  };
}

/** Group a session's raw samples by ordered transition key. */
export function groupSamples(samples: TransitionSample[]): Map<string, TransitionSample[]> {
  const grouped = new Map<string, TransitionSample[]>();
  for (const sample of samples) {
    const list = grouped.get(sample.key);
    if (list) list.push(sample);
    else grouped.set(sample.key, [sample]);
  }
  return grouped;
}
